// src/common/components/DynamicForm/formDynamicTypes.ts

import type { Rule } from "antd/es/form";
import type dayjs from "dayjs";
import type { FieldPattern } from "./ValidationPatterns";


// --- Interface Definitions ---

export interface ApiResponse {
    data: Record<string, unknown>; // Changed from any to unknown for better type safety
}

// Interface for API responses that indicate a business logic error but return an HTTP 200 OK
interface ApiResponseError {
    isError: true;
    message: string;
}

// Type guard to check if the response is an ApiResponseError
export function isApiResponseError(data: unknown): data is ApiResponseError & { data?: any[] } {
    return (
        typeof data === 'object' &&
        data !== null &&
        'isError' in data &&
        (data as { isError: boolean }).isError === true
    );
}

type FieldType =
    | 'input' | 'textarea' | 'password' | 'number'
    | 'switch' | 'select' | 'checkbox' | 'radio' | 'date' | 'datepicker' | 'fileUpload';

// FieldValue type updated to be more precise for Ant Design onChange events
type FieldValue = string | number | boolean | dayjs.Dayjs | null | undefined | (string | number)[];

// Condition for dynamic field visibility/disability/readOnly
export interface FieldCondition {
    field: string;
    operator: '===' | '!==' | '>' | '<' | '>=' | '<=' | 'includes' | 'isTruthy' | 'isFalsy' ;
    value?: unknown; // Changed from any to unknown
}

// Configuration for fetching dynamic options from an API
export type DynamicOptionConfig = {
    url: string;
    method?: 'GET' | 'POST'; // Default to GET
    body?: Record<string, unknown>; // For POST requests, changed from any to unknown
    transformData: string; // A stringified function like "(data) => data.map(item => ({ label: item.name, value: item.id }))"
    dependsOn?: string[]; // New: Array of field names this field's options depend on
    // Note: 'transformData' uses new Function(), ensure schema is from a trusted source.
    onEmpty?: {
        message: string;
        type: string;
    };
};



// Definition of a single form field
export type FormField = {
    name: string;
    label: string;
    type: FieldType;
    placeholder?: string;
    required?: boolean | FieldCondition[];
    requiredMessage?: string; // Custom message for required validation
    disabled?: boolean;
    hidden?: boolean; // Controls CSS display: none
    hideOnEdit?: boolean; // Hide field specifically in edit mode
    hideOnAdd?: boolean; // Hide field specifically in add new mode
    disableOnEdit?: boolean; // Hide field specifically in edit mode
    colSpan?: number; // Ant Design Grid Col span (1-24), default 24 (full width)
    options?: { label: string; value: string | number }[]; // Static options for select, checkbox, radio
    dynamicOptions?: DynamicOptionConfig; // Dynamic options fetching config
    min?: number; // Minimum value for number input validation
    max?: number; // Maximum value for number input validation
    pattern?: FieldPattern; // <--- MODIFIED HERE: Now accepts string (pattern name) or PatternObject
    validation?: Rule | Rule[]; // Direct Ant Design validation rule object or array of rules
    onChange?: (value: FieldValue | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLButtonElement> | unknown) => void; // More generic onChange due to varied AntD events, changed from any to unknown
    autocomplete?: string;
    disabledDateFunction?: string; 
    checkedValue?: string | number;     // value when switch is ON
    uncheckedValue?: string | number;   // value when switch is OF
    // future date disable {disabledDateFunction: '(current) => current && current > dayjs().endOf(\'day\')'}
    // Past date disable {disabledDateFunction: '(current) => current && current < dayjs().startOf(\'day\')'}
    disabledDate?: (current: dayjs.Dayjs) => boolean;
    defaultValue?: FieldValue; // Default value for the field
    // Conditional logic props for show/hide/disable/readOnly
    dependencies?: FieldCondition[]; // Field is hidden if conditions are not met
    readOnlyWhen?: FieldCondition[]; // Field becomes read-only if conditions are met
    disabledWhen?: FieldCondition[]; // Field becomes disabled if conditions are met

    size?: 'large' | 'middle' | 'small'; // For Ant Design components that support size (e.g., Input, Select, DatePicker)
    mode?: 'multiple' | 'tags'; // Specifically for Select component's mode

    // Props for fileUpload type
  
    uploadUrl?: string;
    accept?: string;
    maxCount?: number;
    responseValueKey?: string;
    fileKey?: string;
    rules?: Rule[];
    showSearch?: boolean; // Enable search functionality for Select component
    updateFieldName?: string;
};

// Definition of a form section (group of fields)
export interface FormSection {
    key: string; // Unique key for the section
    title?: string; // Title for the section, if it's a Card
    type?: 'group' | 'card'; // 'group' for simple grouping (no visible wrapper), 'card' for Ant Card style
    fields: FormField[];
    layout?: {
        type?: 'grid' | 'vertical'; // 'grid' uses Ant Design Row/Col, 'vertical' uses Space
        gutter?: number | [number, number]; // Gutter for 'grid' layout
    };
}
